import { motion } from "framer-motion";
import { useLang } from "@/context/LangContext";

const HowToUse = () => {
  const { t } = useLang();
  return (
    <section
      id="howto"
      data-testid="howto-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#F2EEE3]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-6 items-center mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-7">
            <div className="font-eyebrow text-2xl md:text-[30px] text-[#0F3D2E]/85 mb-6">{t.howto.eyebrow}</div>
            <h2 className="font-serif-editorial text-[#0F3D2E] leading-[0.95] text-[10vw] sm:text-[8vw] md:text-[5.6vw] lg:text-[4.4vw]">
              {t.howto.title.split("\n").map((l, i) => (
                <span key={i} className="block">
                  {i === 1 ? <em className="italic text-[#2E8B4A]">{l}</em> : l}
                </span>
              ))}
            </h2>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 30, scale: 0.95 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 md:col-span-5 relative aspect-[4/3] md:aspect-[5/4] rounded-2xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(15,61,46,0.35)]"
            data-testid="howto-accent-image"
          >
            <img
              src="https://images.pexels.com/photos/2255935/pexels-photo-2255935.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Fresh vegetables"
              className="w-full h-full object-cover"
              draggable="false"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0F3D2E]/25 to-transparent" />
          </motion.div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {t.howto.steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: i * 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={`col-span-12 md:col-span-4 relative`}
              data-testid={`howto-step-${step.n}`}
            >
              <div className="relative bg-[#FAF7F0] rounded-2xl border border-[#E5DFCE] h-full flex flex-col overflow-hidden">
                {/* Step image */}
                <div className="relative w-full aspect-[4/3] bg-transparent overflow-hidden">
                  <img
                    src={`/steps/step${i + 1}.png`}
                    alt={step.title}
                    className="w-full h-full object-contain p-6"
                    draggable="false"
                  />
                  <div className="absolute top-4 left-4 w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#0F3D2E] text-[#FAF7F0] flex items-center justify-center font-ubuntu font-bold text-3xl md:text-4xl shadow-md">
                    {step.n}
                  </div>
                </div>

                <div className="p-8 md:p-10 flex flex-col flex-1 text-center">
                  <h3 className="font-serif-editorial font-bold text-[#0F3D2E] text-3xl md:text-[32px] leading-tight mb-4">
                    {step.title}
                  </h3>
                  <p className="font-ubuntu text-[16px] md:text-[17px] leading-relaxed text-[#0A1F17] mb-6">
                    {step.text}
                  </p>
                  <div className="mt-auto pt-6 border-t border-[#E5DFCE] flex items-center justify-center">
                    <span className="font-eyebrow text-3xl md:text-4xl text-[#2E8B4A]">{step.meta}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowToUse;
